import { Close, KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import {
    Box,
    Chip,
    FormControl,
    Menu,
    styled,
    Typography,
} from "@mui/material";
import { MouseEvent, ReactNode, useEffect, useState } from "react";
import { IListItem } from "../../interface/list-item";
import { IWithClassName, IWithThemeProps } from "../../theme/default.theme";

interface IOwnProps extends IWithClassName {
    children?: ReactNode;
    selectedValue: Array<IListItem>;
    onChangeHandler: (selected: IListItem, isChecked: boolean) => void;
    placeHolder?: string;
    multiline?: boolean;
    disableSearch?: boolean;
    onSearchTextChange?: (text: string) => void;
    getChipText?: (item: IListItem) => string;
    clearAllOption?: boolean;
    onChange?: (list: Array<IListItem>) => void;
    setMenuHeight?: (height: number) => void;
    inputMask?: RegExp | string;
    disable?: boolean;
    options?: Array<IListItem>;
    filteredOptions?: Array<IListItem>;
    onEnterKeyError?: (errorMessage: string) => void;
}

const MultiSelect = ({
    className,
    children,
    selectedValue,
    onChangeHandler,
    placeHolder,
    multiline,
    disableSearch,
    onSearchTextChange,
    getChipText,
    clearAllOption,
    onChange,
    setMenuHeight,
    inputMask,
    disable,
    options,
    filteredOptions,
    onEnterKeyError,
}: IOwnProps) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [topPos, setTopPos] = useState<number>(0);
    const [searchValue, setSearchValue] = useState<string>("");

    const handleClick = (event: MouseEvent<HTMLElement>) => {
        if (disable) return;

        setAnchorEl(event.currentTarget);

        const elementHeight = event.currentTarget.offsetHeight;

        const { top } = event.currentTarget.getBoundingClientRect();

        let height = window.innerHeight * 0.9 - (top + elementHeight);
        if (height < top - window.innerHeight * 0.05) {
            height = top - window.innerHeight * 0.05;
            setTopPos(-window.innerHeight + top);
        } else {
            setTopPos(top + elementHeight);
        }
        setMenuHeight?.(height);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleSearchChange = (input: string) => {
        const filteredInput = inputMask
            ? input.replaceAll(inputMask, "")
            : input;
        setSearchValue(filteredInput);
        onSearchTextChange?.(filteredInput);
    };

    const handleEnterKey = () => {
        if (!searchValue.trim() || !options) {
            onEnterKeyError?.("Incorrect list name");
            return;
        }

        // Use filtered options if available, otherwise fall back to all options
        const optionsToCheck = filteredOptions ?? options;

        // Find exact match by name
        const exactMatch = options.find(
            (option) =>
                option.name.toLowerCase() === searchValue.toLowerCase().trim(),
        );

        if (exactMatch) {
            // Check if already selected
            const isAlreadySelected = selectedValue.some(
                (selected) => selected.id === exactMatch.id,
            );

            if (!isAlreadySelected) {
                onChangeHandler(exactMatch, true);
                setSearchValue("");
                onSearchTextChange?.("");
                setAnchorEl(null); // Close the menu
            }
            return;
        }

        // If no exact match, check if there's only one filtered option remaining
        if (optionsToCheck.length === 1) {
            const singleOption = optionsToCheck[0];

            // Check if already selected
            const isAlreadySelected = selectedValue.some(
                (selected) => selected.id === singleOption.id,
            );

            if (!isAlreadySelected && !singleOption.readonly) {
                onChangeHandler(singleOption, true);
                setSearchValue("");
                onSearchTextChange?.("");
                setAnchorEl(null); // Close the menu
            }
        } else {
            onEnterKeyError?.("Incorrect list name");
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            e.preventDefault();
            handleEnterKey();
        }
    };

    useEffect(() => {
        setSearchValue("");
        onSearchTextChange?.("");
    }, [selectedValue]);

    const selectedLabels = () =>
        selectedValue.length ? (
            selectedValue.map((selectedNode) => (
                <Chip
                    label={
                        getChipText
                            ? getChipText(selectedNode)
                            : selectedNode?.name
                    }
                    key={selectedNode?.id ?? ""}
                    variant="outlined"
                    className="select-label"
                    onDelete={
                        selectedNode && !selectedNode.readonly
                            ? () => onChangeHandler(selectedNode, false)
                            : undefined
                    }
                />
            ))
        ) : (
            <Typography>{placeHolder ? placeHolder : " "}</Typography>
        );

    return (
        <Box className={className}>
            <FormControl>
                <div
                    className="main-label-container default"
                    onClick={handleClick}
                >
                    <div className="label-search-container">
                        <span
                            className={`label-container veritical-center ${
                                multiline ? "break-line" : "overflow-hidden"
                            }`}
                        >
                            {selectedLabels()}
                        </span>
                        {!disableSearch &&
                            (!!anchorEl || !selectedValue.length) && (
                                <input
                                    type="text"
                                    className="search-box"
                                    placeholder="Select Option"
                                    autoFocus={!!anchorEl}
                                    value={searchValue}
                                    onChange={(e) =>
                                        handleSearchChange(
                                            e.currentTarget.value,
                                        )
                                    }
                                    onKeyDown={handleKeyDown}
                                />
                            )}
                    </div>
                    {clearAllOption && (
                        <span className="veritical-center">
                            <Close onClick={() => onChange?.([])} />
                        </span>
                    )}

                    <span className="arrow-container veritical-center">
                        {anchorEl ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                    </span>
                </div>
                <Menu
                    anchorEl={anchorEl}
                    marginThreshold={!disableSearch ? 16 : undefined}
                    open={!!anchorEl}
                    onClose={handleClose}
                    slotProps={{
                        root: {
                            disableAutoFocus: true,
                        },
                    }}
                    sx={{
                        ".MuiPaper-root": {
                            maxHeight:
                                topPos < 0 ? "95%" : `calc(95% - ${topPos}px)`,
                            top: `${topPos < 0 ? "unset" : `${topPos} px`} !important`,
                            ...(topPos < 0
                                ? { bottom: `${-topPos}px !important` }
                                : {}),
                        },
                    }}
                >
                    {children}
                </Menu>
            </FormControl>
        </Box>
    );
};

export default styled(MultiSelect)(({ theme }: IWithThemeProps) => ({
    width: "inherit",

    ".main-label-container": {
        display: "flex",
        minHeight: 40,
        width: "100%",
    },

    ".overflow-hidden": {
        overflow: "hidden",
    },

    ".break-line": {
        flexWrap: "wrap",
    },

    ".label-container": {
        display: "flex",
        gap: 8,
        padding: theme?.spacing(0.25),
    },

    ".arrow-container": {
        position: "absolute",
        right: 0,
        height: "100%",
    },

    ".veritical-center svg": {
        height: 25,
        color: theme?.palette.detailsPane.borderBottom,
        cursor: "pointer",
    },

    ".default": {
        borderBottom: `1px solid ${
            theme?.palette.detailsPane.borderBottom ?? ""
        }`,
    },

    ".default:hover": {
        borderBottom: `2px solid ${
            theme?.palette.detailsPane.borderBottom ?? ""
        }`,
    },

    ".veritical-center": {
        display: "flex",
        alignItems: "center",
    },

    ".MuiTypography-root": {
        color: theme?.palette.history.textColor,
    },

    ".MuiFormControl-root": {
        width: "100%",
    },

    ".select-label": {
        zIndex: 100,
    },

    ".search-box": {
        width: "100%",
        border: "none",
        marginTop: theme?.spacing(1) ?? 0,
    },

    ".search-box:focus-visible": {
        border: "none",
        outline: "none",
    },
    ".label-search-container": {
        width: "calc(100% - 50px)",
    },
}));
